/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import styles from '@app/components/modals/shared/AddOverridePopout.module.css';
import {DEFAULT_ROLE_COLOR_HEX, getRoleColor} from '@app/components/modals/shared/PermissionComponents';
import {Avatar} from '@app/components/uikit/Avatar';
import {
	SearchableListPopout,
	type SearchableListPopoutItem,
	type SearchableListPopoutSection,
} from '@app/components/uikit/popout/searchable_list_popout/SearchableListPopout';
import GuildMemberStore from '@app/stores/GuildMemberStore';
import GuildStore from '@app/stores/GuildStore';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo} from 'react';

interface AddOverridePopoutProps {
	guildId: string;
	existingOverwriteIds: Set<string>;
	onSelect: (id: string, type: 0 | 1, name: string) => void;
	onClose: () => void;
}

export const AddOverridePopout: React.FC<AddOverridePopoutProps> = observer(function AddOverridePopout({
	guildId,
	existingOverwriteIds,
	onSelect,
	onClose,
}) {
	const {t} = useLingui();
	const guild = GuildStore.getGuild(guildId);

	const roles = useMemo(() => {
		if (!guild) return [];
		return Object.values(guild.roles)
			.filter((role) => !existingOverwriteIds.has(role.id))
			.sort((a, b) => b.position - a.position);
	}, [guild, existingOverwriteIds]);

	// Load the full member list (not just the online/cached subset) so every member is searchable.
	useEffect(() => {
		void GuildMemberStore.fetchMembers(guildId, {limit: 1000, presences: false}).catch(() => {});
	}, [guildId]);

	const allMembers = GuildMemberStore.getMembers(guildId);
	const members = useMemo(() => {
		if (!guild) return [];
		return allMembers
			.filter((member) => !existingOverwriteIds.has(member.user.id))
			.sort((a, b) => a.user.username.localeCompare(b.user.username));
	}, [guild, allMembers, existingOverwriteIds]);

	const roleItems = useMemo<Array<SearchableListPopoutItem>>(() => {
		return roles.map((role) => ({
			id: `role-${role.id}`,
			ariaLabel: role.name,
			searchValues: [role.name],
			onSelect: () => {
				onSelect(role.id, 0, role.name);
				onClose();
			},
			render: () => (
				<>
					<div
						className={styles.roleIndicator}
						style={{
							backgroundColor: role.color === 0 ? DEFAULT_ROLE_COLOR_HEX : getRoleColor(role.color),
						}}
					/>
					<span className={styles.itemLabel}>{role.name}</span>
				</>
			),
		}));
	}, [onClose, onSelect, roles]);

	const memberItems = useMemo<Array<SearchableListPopoutItem>>(() => {
		return members.map((member) => {
			const displayName = member.nick ?? member.user.globalName;
			const tag = member.user.tag;
			return {
				id: `member-${member.user.id}`,
				ariaLabel: displayName ? `${displayName} (${tag})` : tag,
				searchValues: [displayName, member.user.username, tag].filter((value): value is string => Boolean(value)),
				onSelect: () => {
					onSelect(member.user.id, 1, displayName ?? member.user.username);
					onClose();
				},
				render: () => (
					<>
						<Avatar user={member.user} size={12} className={styles.avatar} guildId={guildId} />
						<span className={styles.itemLabel}>
							{displayName ? (
								<>
									{displayName} <span style={{opacity: 0.6}}>{tag}</span>
								</>
							) : (
								tag
							)}
						</span>
					</>
				),
			};
		});
	}, [guildId, members, onClose, onSelect]);

	const sections = useMemo<Array<SearchableListPopoutSection>>(() => {
		const nextSections: Array<SearchableListPopoutSection> = [];
		if (roleItems.length > 0) {
			nextSections.push({
				id: 'roles',
				heading: <Trans>Roles</Trans>,
				items: roleItems,
			});
		}
		if (memberItems.length > 0) {
			nextSections.push({
				id: 'members',
				heading: <Trans>Members</Trans>,
				items: memberItems,
			});
		}
		return nextSections;
	}, [memberItems, members.length, roleItems]);

	return (
		<SearchableListPopout
			className={styles.popoutContainer}
			searchClassName={styles.searchContainer}
			scrollerClassName={styles.scroller}
			sectionClassName={styles.section}
			sectionHeadingClassName={styles.sectionHeader}
			optionClassName={styles.itemButton}
			emptyStateClassName={styles.emptyState}
			placeholder={t`Search roles or members...`}
			searchInputAriaLabel={t`Search roles or members`}
			listAriaLabel={t`Roles and members`}
			noResultsLabel={<Trans>No results found</Trans>}
			sections={sections}
			onRequestClose={onClose}
		/>
	);
});
